/* File: login.jsx
 * Path: ananunadm/src/components/login.jsx
*/
import { getTenantKey } from "../helpers/tenant";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../helpers/auth";
import {
	TextInput,
	Select,
	PasswordInput,
	Anchor,
	Paper,
	Title,
	Text,
	Container,
	Button,
	Box,
	Checkbox,
	Stack,
	Group,
	UnstyledButton,
	Avatar,
	ActionIcon,
	useMantineTheme
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";
import { useLang } from "../helpers/language";
import { T } from "../helpers/translator";
import ResetPasswordPage from "./auth/ResetPasswordPage";
import { DEFAULT_USER_IMAGE_SRC, getUserImageSrc } from "../helpers/uploadImage";
import { getSavedLoginUsers, saveLoginUser, removeSavedLoginUser } from "../helpers/savedLoginUsers";

/** Prefer active locale, then English/Armenian/Russian, then first+last name. */
function resolveDisplayName(user, lang) {
	if (!user) return "";
	const localizedName = user.name?.[lang] || user.name?.en || user.name?.hy || user.name?.ru;
	if (localizedName) return localizedName;
	const firstName = user.firstName?.[lang] || user.firstName?.en;
	const lastName = user.lastName?.[lang] || user.lastName?.en;
	if (firstName || lastName) return [firstName, lastName].filter(Boolean).join(" ");
	return user.username || "";
}

function initialsFromName(name = "") {
	const parts = String(name).trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return "?";
	return parts
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");
}

export default function Login() {
	const domainTenant = getTenantKey();
	const [tenant, setTenant] = useState(domainTenant);
	let [username, setUsername] = useState("");
	let [password, setPassword] = useState("");
	let { lang } = useLang();
	const { login } = useAuth();
	const theme = useMantineTheme();
	const [loading, setLoading] = useState(false);
	const location = useLocation();

	const [savedUsers, setSavedUsers] = useState(() => getSavedLoginUsers());
	const [pickerMode, setPickerMode] = useState(() => (getSavedLoginUsers().length > 0 ? "picker" : "form"));
	const [selectedSavedUser, setSelectedSavedUser] = useState(null);
	const [rememberMe, setRememberMe] = useState(true);

	function handleUsernameChange(event) {
		setUsername(event.target.value);
	}
	function handlePasswordChange(event) {
		setPassword(event.target.value);
	}

	function selectSavedUser(entry) {
		setSelectedSavedUser(entry);
		setUsername(entry.username);
		setTenant(domainTenant || entry.tenant || "");
		setPassword("");
		setPickerMode("password");
	}

	function useAnotherAccount() {
		setSelectedSavedUser(null);
		setUsername("");
		setPassword("");
		setPickerMode("form");
	}

	function backToSavedAccounts() {
		setSelectedSavedUser(null);
		setUsername("");
		setPassword("");
		setPickerMode("picker");
	}

	function forgetSavedUser(event, entryUsername, entryTenant) {
		event.stopPropagation();
		const next = removeSavedLoginUser(entryUsername, entryTenant);
		setSavedUsers(next);
		if (!next.length) {
			setPickerMode("form");
		}
	}

	async function handleSubmit(event) {
		event.preventDefault();
		setLoading(true);
		let result;
		try {
			result = tenant ? await login(username, password, tenant) : { success: false, message: "Select your platform." };
		} finally {
			setLoading(false);
		}
		if (!result?.success) {
			notifications.show({
				title:
					result?.error === "ACCOUNT_LOCKED"
						? "Account locked"
						: T("loginFailed", lang),
				message:
					result?.message ||
					T("loginFailedMessage", lang),
				autoClose: 2500,
				color: "red"
			});
			return;
		}

		const shouldRemember = pickerMode === "password" ? true : rememberMe;
		if (shouldRemember && result.user) {
			const next = saveLoginUser({
				username: result.user.username || username,
				tenant,
				displayName: resolveDisplayName(result.user, lang),
				avatarUrl: getUserImageSrc(result.user, { targetWidth: 64 }),
				userId: result.user._id
			});
			setSavedUsers(next);
		}

		if (!result?.passwordChangeRequired) {
			notifications.show({
				title: T("loginSuccess", lang),
				message: T("loginSuccessMessage", lang),
				autoClose: 2500
			});
		} else {
			notifications.show({
				title: "Password change required",
				message: "Sign-in succeeded, but a password change is required before platform access.",
				autoClose: 3500,
				color: "orange"
			});
		}
	}

	const isDark = theme.colorScheme === "dark";

	if (location.pathname.startsWith("/reset-password")) {
		return <ResetPasswordPage />;
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: isDark
					? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 40%, ${theme.fn.rgba(theme.colors.cyan[9], 0.15)} 100%)`
					: `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.white} 40%, ${theme.fn.rgba(theme.colors.cyan[0], 0.5)} 100%)`,
				position: "relative",
				overflow: "hidden"
			}}
		>
			{/* Decorative gradient orbs */}
			<Box
				sx={{
					position: "absolute",
					top: -120,
					right: -120,
					width: 350,
					height: 350,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${theme.fn.rgba(theme.colors.cyan[5], 0.12)} 0%, transparent 70%)`,
					filter: "blur(40px)",
					pointerEvents: "none"
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					bottom: -80,
					left: -80,
					width: 280,
					height: 280,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${theme.fn.rgba(theme.colors.teal[5], 0.1)} 0%, transparent 70%)`,
					filter: "blur(40px)",
					pointerEvents: "none"
				}}
			/>

			<Container size={420} className="apit-fade-in-up" sx={{ position: "relative", zIndex: 1 }}>
				<Title align="center" mb="xs">
					<img src="/logo.svg" alt="main page logo" style={{ maxHeight: 60 }} />
				</Title>
				<Text color="dimmed" size="sm" align="center" mt={5}>
					{T("writeUsIfThereAreAnyQuestions", lang)}{" "}
					<Anchor href="mailto:support@deepmindsystems.com" size="sm" component="button">
						support@deepmindsystems.com
					</Anchor>
				</Text>

				<Paper
					withBorder
					shadow="xl"
					p={36}
					mt={30}
					radius="lg"
					sx={{
						width: 370,
						maxWidth: "100%",
						backdropFilter: "blur(10px)",
						background: isDark
							? theme.fn.rgba(theme.colors.dark[7], 0.85)
							: theme.fn.rgba(theme.white, 0.9),
						borderColor: isDark
							? theme.fn.rgba(theme.colors.dark[4], 0.3)
							: theme.fn.rgba(theme.colors.gray[3], 0.5)
					}}
				>
					{pickerMode === "picker" && (
						<Stack gap="xs">
							<Text size="sm" color="dimmed">
								Choose an account
							</Text>
							{savedUsers.map((entry) => (
								<UnstyledButton
									key={`${entry.tenant}:${entry.username}`}
									onClick={() => selectSavedUser(entry)}
									sx={{
										display: "flex",
										alignItems: "center",
										width: "100%",
										padding: 8,
										borderRadius: theme.radius.md,
										"&:hover": {
											background: isDark ? theme.colors.dark[6] : theme.colors.gray[0]
										}
									}}
								>
									<Avatar src={entry.avatarUrl || DEFAULT_USER_IMAGE_SRC} radius="xl" size="md" color="cyan">
										{initialsFromName(entry.displayName)}
									</Avatar>
									<Box sx={{ flex: 1, marginLeft: 12, textAlign: "left", overflow: "hidden" }}>
										<Text size="sm" weight={600} truncate>
											{entry.displayName}
										</Text>
										<Text size="xs" color="dimmed" truncate>
											{entry.username} · {entry.tenant || "Select platform"}
										</Text>
									</Box>
									<ActionIcon
										variant="subtle"
										color="gray"
										onClick={(event) => forgetSavedUser(event, entry.username, entry.tenant)}
										aria-label={`Forget ${entry.displayName}`}
									>
										<IconX size="0.9rem" />
									</ActionIcon>
								</UnstyledButton>
							))}
							<Button variant="subtle" color="gray" mt="xs" onClick={useAnotherAccount}>
								Use another account
							</Button>
						</Stack>
					)}

					{!domainTenant && pickerMode !== "picker" && (
                        <Select label="Platform" placeholder="Select your platform" required mb="md"
                            data={['volios', 'margaryan', 'webradio', 'admin']} value={tenant} onChange={setTenant} />
                    )}
                    {pickerMode === "password" && (
						<>
							<Group gap="sm" mb="md">
								<Avatar src={selectedSavedUser?.avatarUrl || DEFAULT_USER_IMAGE_SRC} radius="xl" size="md" color="cyan">
									{initialsFromName(selectedSavedUser?.displayName)}
								</Avatar>
								<Box sx={{ overflow: "hidden" }}>
									<Text size="sm" weight={600} truncate>
										{selectedSavedUser?.displayName}
									</Text>
									<Anchor size="xs" component="button" type="button" onClick={backToSavedAccounts}>
										Not you? Switch account
									</Anchor>
								</Box>
							</Group>
							<PasswordInput
								label={T("password", lang)}
								placeholder={T("password", lang)}
								size="md"
								autoFocus
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										handleSubmit(e);
									}
								}}
								value={password}
								onChange={handlePasswordChange}
								visible={false}
							/>
							<Button
								onClick={handleSubmit}
								fullWidth
								mt="xl"
								size="md"
								loading={loading}
								variant="gradient"
								gradient={{ from: "cyan", to: "teal", deg: 135 }}
								sx={{
									"&:hover": {
										transform: "translateY(-1px)",
										boxShadow: `0 4px 12px ${theme.fn.rgba(theme.colors.cyan[5], 0.35)}`
									}
								}}
							>
								{T("login", lang)}
							</Button>
						</>
					)}

					{pickerMode === "form" && (
						<>
							<TextInput
								value={username}
								onChange={handleUsernameChange}
								label={T("username", lang)}
								placeholder="username"
								required
								size="md"
							/>
							<PasswordInput
								label={T("password", lang)}
								placeholder={T("password", lang)}
								mt="md"
								size="md"
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										handleSubmit(e);
									}
								}}
								value={password}
								onChange={handlePasswordChange}
								visible={false}
							/>
							<Text color="dimmed" size="xs" mt={8}>
								Leave the password blank only if this is a brand-new account that does not have a password yet.
							</Text>
							<Checkbox
								mt="md"
								label="Remember this account on this device"
								checked={rememberMe}
								onChange={(event) => setRememberMe(event.currentTarget.checked)}
							/>
							<Button
								onClick={handleSubmit}
								fullWidth
								mt="xl"
								size="md"
								loading={loading}
								variant="gradient"
								gradient={{ from: "cyan", to: "teal", deg: 135 }}
								sx={{
									"&:hover": {
										transform: "translateY(-1px)",
										boxShadow: `0 4px 12px ${theme.fn.rgba(theme.colors.cyan[5], 0.35)}`
									}
								}}
							>
								{T("login", lang)}
							</Button>
							{savedUsers.length > 0 && (
								<Button variant="subtle" color="gray" fullWidth mt="sm" onClick={backToSavedAccounts}>
									Back to saved accounts
								</Button>
							)}
						</>
					)}
				</Paper>
				<Text color="dimmed" size="xs" align="center" mt={20}>
					{T("developedBy", lang)}{" "}
					<Anchor href="https://www.deepmindsystems.com" size="xs" color="cyan">
						www.deepmindsystems.com
					</Anchor>
				</Text>
			</Container>
		</Box>
	);
}
